import { useState, useEffect, useMemo } from 'react';
import Fuse from 'fuse.js';

export const useFuzzyAddressSearch = (allForms, currentFormId = null) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [similarForms, setSimilarForms] = useState([]);

  // Prepare the forms for fuzzy searching
  const formsForSearch = useMemo(() => {
    if (!allForms || typeof allForms !== 'object') {
      return [];
    }
    
    const formsArray = Object.keys(allForms)
      .map(formId => {
        const form = allForms[formId];
        return {
          ...form,
          id: formId
        };
      });
      
    const filteredForms = formsArray.filter(form => {
        // Exclude submitted forms and the current form being edited
        const isNotSubmitted = !form.submissionTime || form.submissionTime === null;
        const isNotCurrentForm = form.id !== currentFormId;
        const hasAddress = form.address && form.address.trim() !== '';
        
        return isNotSubmitted && isNotCurrentForm && hasAddress;
      });
      
    return filteredForms;
  }, [allForms, currentFormId]);

  // Configure Fuse.js for fuzzy searching
  const fuse = useMemo(() => {
    const options = {
      keys: ['address'],
      threshold: 0.1, // Adjust this value to control search sensitivity (0.0 = exact match, 1.0 = match anything)
      distance: 100, // Max distance for fuzzy matching in characters
      location: 0, // Location of the match in the text eg. beginning of the string 
      ignoreLocation: true,
      includeScore: true,
      minMatchCharLength: 3 // Minimum length of the query to trigger a search
    };
    
    return new Fuse(formsForSearch, options);
  }, [formsForSearch]);

  // Perform fuzzy search when searchQuery changes
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 5) {
      setSimilarForms([]);
      return;
    }

    const results = fuse.search(searchQuery.trim());
    // Filter results to show only reasonably good matches (score < 0.6)
    const goodMatches = results
      .filter(result => result.score < 0.6)
      .map(result => result.item);
    
    setSimilarForms(goodMatches);
  }, [searchQuery, fuse]);

  return {
    searchQuery,
    setSearchQuery,
    similarForms
  };
};
